import React, { createContext, useState, useEffect, useContext } from 'react';
import { cartService } from '../services/api';
import { useAuth } from './AuthContext';

// Create cart context
export const CartContext = createContext();

// Custom hook to use cart context
export const useCart = () => {
  return useContext(CartContext);
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { isAuthenticated } = useAuth();

  // Fetch cart items when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchCartItems();
    } else {
      // If not authenticated, try to get cart from localStorage
      const storedCart = localStorage.getItem('cart');
      if (storedCart) {
        setCartItems(JSON.parse(storedCart));
      }
    }
  }, [isAuthenticated]);

  // Save cart to localStorage when it changes (for non-authenticated users)
  useEffect(() => {
    if (!isAuthenticated && cartItems.length > 0) {
      localStorage.setItem('cart', JSON.stringify(cartItems));
    }
  }, [cartItems, isAuthenticated]);

  // Fetch cart items from API
  const fetchCartItems = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await cartService.getCartItems();
      setCartItems(response.data);
    } catch (error) {
      console.error('Fetch cart error:', error);
      setError('Failed to load cart items');
    } finally {
      setLoading(false);
    }
  };

  // Add item to cart
  const addToCart = async (product, quantity = 1) => {
    setError('');

    if (isAuthenticated) {
      try {
        await cartService.addToCart(product.id, quantity);
        fetchCartItems();
      } catch (error) {
        console.error('Add to cart error:', error);
        setError(error.response?.data?.message || 'Failed to add item to cart');
      }
    } else {
      // For non-authenticated users, manage cart in localStorage
      const existingItem = cartItems.find(item => item.product_id === product.id);

      if (existingItem) {
        // Update quantity if item exists
        setCartItems(
          cartItems.map(item =>
            item.product_id === product.id
              ? { ...item, quantity: item.quantity + quantity }
              : item
          )
        );
      } else {
        // Add new item
        setCartItems([
          ...cartItems,
          {
            id: Date.now(), // Temporary ID
            product_id: product.id,
            name: product.name,
            price: product.price,
            image_url: product.image_url,
            quantity
          }
        ]);
      }
    }
  };

  // Update cart item quantity
  const updateCartItemQuantity = async (itemId, quantity) => {
    setError('');

    if (isAuthenticated) {
      try {
        await cartService.updateCartItem(itemId, quantity);
        fetchCartItems();
      } catch (error) {
        console.error('Update cart error:', error);
        setError(error.response?.data?.message || 'Failed to update cart');
      }
    } else {
      // For non-authenticated users
      setCartItems(
        cartItems.map(item =>
          item.id === itemId ? { ...item, quantity } : item
        )
      );
    }
  };

  // Increase item quantity
  const increaseItemQuantity = async (itemId) => {
    const item = cartItems.find(item => item.id === itemId);
    if (item) {
      updateCartItemQuantity(itemId, item.quantity + 1);
    }
  };

  // Decrease item quantity
  const decreaseItemQuantity = async (itemId) => {
    const item = cartItems.find(item => item.id === itemId);
    if (item && item.quantity > 1) {
      updateCartItemQuantity(itemId, item.quantity - 1);
    } else if (item && item.quantity <= 1) {
      removeItem(itemId);
    }
  };

  // Remove item from cart
  const removeItem = async (itemId) => {
    setError('');

    if (isAuthenticated) {
      try {
        await cartService.removeFromCart(itemId);
        fetchCartItems();
      } catch (error) {
        console.error('Remove from cart error:', error);
        setError(error.response?.data?.message || 'Failed to remove item from cart');
      }
    } else {
      // For non-authenticated users
      setCartItems(cartItems.filter(item => item.id !== itemId));
    }
  };

  // Clear cart
  const clearCart = async () => {
    setError('');

    if (isAuthenticated) {
      try {
        await cartService.clearCart();
        setCartItems([]);
      } catch (error) {
        console.error('Clear cart error:', error);
        setError(error.response?.data?.message || 'Failed to clear cart');
      }
    } else {
      // For non-authenticated users
      setCartItems([]);
      localStorage.removeItem('cart');
    }
  };

  // Calculate cart total
  const cartTotal = cartItems.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );

  // Calculate total items in cart
  const cartItemsCount = cartItems.reduce(
    (count, item) => count + item.quantity,
    0
  );

  // Format cart to match what the CartPage expects
  const cart = {
    items: cartItems,
    totalQuantity: cartItemsCount,
    totalAmount: cartTotal
  };

  // Context value
  const value = {
    cart,
    cartItems,
    cartTotal,
    cartItemsCount,
    loading,
    error,
    addToCart,
    updateCartItemQuantity,
    increaseItemQuantity,
    decreaseItemQuantity,
    removeItem,
    clearCart,
    fetchCartItems
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartProvider; 